import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, of, concatMap, tap } from 'rxjs';
import { LoginService } from '../../services/login.service';
import { AuthenticationApiActions, AuthenticationUiActions } from '../actions';
import { IUser } from '../../models/User';
import { ToastService } from '../../../../shared/services/toast.service';
import { MetadataSchemaService } from '../../../../shared/services/metadata-schema.service';
import { DatasetDrawerState } from '../../../datasets/components/dataset-drawer/drawer-state';
import { SessionTeardownService } from '../../../../shared/utils/clear-session-storage';
import { SessionActions } from '../../../../shared/state/session/session.actions';

@Injectable()
export class AuthenticationEffects {
  actions$ = inject(Actions);
  private toastService = inject(ToastService);
  private datasetDrawerState = inject(DatasetDrawerState);
  private sessionTeardown = inject(SessionTeardownService);
  private metadataSchemaService = inject(MetadataSchemaService);

  constructor(private loginService: LoginService) {}

  getAuthUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationUiActions.GetAuthUser),
      concatMap(() => {
        return this.loginService.getAuthUser().pipe(
          map((user: IUser) => AuthenticationApiActions.GetAuthUserSuccess({ user })),
          catchError((error) => {
            this.toastService.show('Failed to fetch user session', 'error');
            return of(AuthenticationApiActions.GetAuthUserError(error));
          })
        );
      })
    )
  );

  postLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationUiActions.LogUserOut),
      concatMap(() => {
        return this.loginService.postLogout().pipe(
          map(() => AuthenticationApiActions.LogoutUserSuccess()),
          catchError((error) => {
            this.toastService.show('Logout failed', 'error');
            return of(AuthenticationApiActions.LogoutUserError(error));
          })
        );
      })
    )
  );

  // Flush the in-memory metadata schema cache whenever the active session
  // changes. The schema is cached for the lifetime of the SPA, so without this
  // a schema edit made by another role (e.g. a platform admin adding a new
  // post-analysis tag) would not appear after logging back in until a full
  // browser reload wiped the cache.
  refreshSchemaOnAuthChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthenticationApiActions.GetAuthUserSuccess, AuthenticationApiActions.LogoutUserSuccess),
        tap(() => this.metadataSchemaService.invalidate())
      ),
    { dispatch: false }
  );

  logoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationApiActions.LogoutUserSuccess),
      tap(() => {
        this.datasetDrawerState.setSelectedSequenceFileIds([]);
        this.sessionTeardown.clearStorage();
        // Leave the app rather than routing within it. The store and the
        // rehydrate snapshot both outlive a router.navigate(), which is how
        // the previous user's lab used to survive into the next session.
        this.sessionTeardown.reloadToLogin();
      }),
      // Reset the store as well, so anything dispatched between here and the
      // browser actually unloading re-serializes an already-empty store rather
      // than writing the outgoing user's slices back to disk.
      map(() => SessionActions.ResetSessionState())
    )
  );
}
